import Swal from 'sweetalert2';

// Configuração padrão do SweetAlert2
const defaultConfig = {
  customClass: {
    confirmButton: 'bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg',
    cancelButton: 'bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded-lg',
    actions: 'gap-4',
    popup: 'rounded-lg shadow-xl max-h-screen overflow-y-auto sm:max-h-none sm:overflow-visible',
    title: 'text-gray-900 font-semibold',
    content: 'text-gray-600'
  },
  buttonsStyling: false,
  reverseButtons: true
};

class AlertService {
  // Alerta de sucesso
  success(title: string, text?: string): Promise<any> {
    // Padrão global: toast no topo direito
    return Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title,
      text,
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      }
    });
  }

  // Alerta de erro
  error(title: string, text?: string): Promise<any> {
    // Padrão global: toast no topo direito
    return Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'error',
      title,
      text,
      showConfirmButton: false,
      timer: 4000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      }
    });
  }

  // Alerta de aviso
  warning(title: string, text?: string): Promise<any> {
    // Padronizar como toast (canto superior direito), igual ao da imagem
    return Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'warning',
      title,
      text,
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      }
    });
  }

  // Alerta de informação
  info(title: string, text?: string): Promise<any> {
    // Padrão global: toast no topo direito
    return Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'info',
      title,
      text,
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      }
    });
  }

  // Confirmação de ação
  confirm(
    title: string,
    text?: string,
    confirmButtonText: string = 'Sim, confirmar',
    cancelButtonText: string = 'Cancelar'
  ): Promise<any> {
    return Swal.fire({
      ...defaultConfig,
      icon: 'question',
      title,
      text,
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText,
      focusCancel: true
    });
  }

  // Confirmação de exclusão
  confirmDelete(
    title: string = 'Tem certeza?',
    text: string = 'Esta ação não pode ser desfeita!',
    confirmButtonText: string = 'Sim, excluir',
    cancelButtonText: string = 'Cancelar'
  ): Promise<any> {
    return Swal.fire({
      ...defaultConfig,
      icon: 'warning',
      title,
      text,
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText,
      customClass: {
        ...defaultConfig.customClass,
        confirmButton: 'bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg'
      },
      focusCancel: true
    });
  }

  // Toast notification (canto superior direito)
  toast(
    icon: 'success' | 'error' | 'warning' | 'info',
    title: string,
    timer: number = 3000
  ): Promise<any> {
    return Swal.fire({
      toast: true,
      position: 'top-end',
      icon,
      title,
      showConfirmButton: false,
      timer,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      }
    });
  }

  // Loading/Progress
  loading(title: string = 'Carregando...', text?: string, html?: string): void {
    Swal.fire({
      ...defaultConfig,
      title,
      text,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });
  }

  loadingTime(title: string = 'Carregando...', text?: string, html?: string): void {
    Swal.fire({
      ...defaultConfig,
      title,
      text,
      html,
      timer: 2000,
      timerProgressBar: true,
      didOpen: () => {
        Swal.showLoading();
      },
      willClose: () => {}
    });
  }

  // Fechar loading
  close(): void {
    Swal.close();
  }

  // Input de texto
  input(
    title: string,
    inputPlaceholder: string = '',
    inputValue: string = '',
    confirmButtonText: string = 'Confirmar',
    cancelButtonText: string = 'Cancelar'
  ): Promise<any> {
    return Swal.fire({
      ...defaultConfig,
      title,
      input: 'text',
      inputPlaceholder,
      inputValue,
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText,
      inputValidator: (value) => {
        if (!value) {
          return 'Você precisa digitar algo!';
        }
        return null;
      }
    });
  }

  // Select/Dropdown
  select(
    title: string,
    options: Record<string, string>,
    confirmButtonText: string = 'Confirmar',
    cancelButtonText: string = 'Cancelar'
  ): Promise<any> {
    return Swal.fire({
      ...defaultConfig,
      title,
      input: 'select',
      inputOptions: options,
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText,
      inputValidator: (value: any) => {
        if (!value) {
          return 'Você precisa selecionar uma opção!';
        }
        return null;
      }
    });
  }
}

export const alertService = new AlertService();
export default alertService;